import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DownloadOutlined,
  RollbackOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import { errorText, fileTitle, saveBlob } from '../format';
import FxCheckAlert from './FxCheckAlert';
import MailPreview from './MailPreview';
import RecipientsFields from './RecipientsFields';

const { Text } = Typography;

const STATUS_COLOR = {
  published: 'green',
  replaced: 'default',
  withdrawn: 'red',
};

/**
 * Publishing the month's pack to the client: the files are frozen, the
 * client's users get an email and see them under "My reports". A second
 * publication of the same month replaces the first; a release can be taken
 * back. The dialog lets the accountant choose who gets it — recipients
 * outside the platform, HLB copies, whether the client's users are told —
 * starting from last time's choice, which the publication saves again.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {Object} props.period - dayjs month being prepared
 * @param {number|null} props.fxRate - Rate override used for the pack
 * @param {boolean} props.canPublish - Whether the pack can be built for the month
 * @returns {JSX.Element} The releases panel
 */
export default function ReleasesPanel({
  companyId,
  period,
  fxRate,
  canPublish,
}) {
  const { t } = useTranslation('clientReports');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [copies, setCopies] = useState([]);
  const [notifyClientUsers, setNotifyClientUsers] = useState(true);
  // The accountant confirms the month is posted and checked; the server
  // refuses a publication without it.
  const [confirmed, setConfirmed] = useState(false);
  const year = period.year();
  const month = period.month() + 1;
  const label = period.format('MM.YYYY');

  const { data, isLoading } = useQuery({
    queryKey: ['client-reports-releases', companyId],
    queryFn: () => API.clientReports.getReleases(companyId),
  });
  const releases = data?.data || [];
  const { data: profileData } = useQuery({
    queryKey: ['client-reports-pack-profile', companyId],
    queryFn: () => API.clientReports.getReportProfile(companyId),
  });
  const profile = profileData?.data;
  const fileOptions = [...new Set((profile?.reports || []).map(fileTitle))].map(
    (title) => ({ value: title, label: title })
  );
  const current = releases.find(
    (r) => r.year === year && r.month === month && r.status === 'published'
  );

  // Every opening starts from the choice saved on the pack; a refetch while
  // the dialog is open must not undo what the accountant changed.
  const openDialog = () => {
    setRecipients(profile?.externalRecipients || []);
    setCopies(profile?.copyRecipients || []);
    setNotifyClientUsers(profile?.notifyClientUsers !== false);
    setConfirmed(false);
    setOpen(true);
  };

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: ['client-reports-releases', companyId],
    });
  const onError = async (e) => message.error(await errorText(e));

  const publishMutation = useMutation({
    mutationFn: () =>
      API.clientReports.publishRelease(companyId, {
        year,
        month,
        confirmed,
        fxRate: fxRate || undefined,
        note: note.trim() || undefined,
        externalRecipients: recipients.filter((r) => r.email?.trim()),
        copyRecipients: copies,
        notifyClientUsers,
      }),
    onSuccess: (res) => {
      setOpen(false);
      setNote('');
      refresh();
      queryClient.invalidateQueries({
        queryKey: ['client-reports-pack-profile', companyId],
      });
      message.success(
        t('release.published', { count: res.data.notifiedCount })
      );
      const failed = (res.data.deliveries || []).filter(
        (d) => d.status !== 'queued'
      );
      if (failed.length) {
        message.warning(
          t('release.deliveryFailed', {
            emails: failed.map((d) => d.email).join(', '),
          })
        );
      }
    },
    onError,
  });
  const withdrawMutation = useMutation({
    mutationFn: (releaseId) =>
      API.clientReports.withdrawRelease(companyId, releaseId),
    onSuccess: () => {
      refresh();
      message.success(t('release.withdrawn'));
    },
    onError,
  });
  const downloadMutation = useMutation({
    mutationFn: ({ releaseId, index }) =>
      API.clientReports.downloadReleaseFile(companyId, releaseId, index),
    onSuccess: saveBlob,
    onError,
  });

  const columns = [
    {
      title: t('release.period'),
      key: 'period',
      width: 110,
      render: (_, r) => `${String(r.month).padStart(2, '0')}.${r.year}`,
    },
    {
      title: t('release.version'),
      dataIndex: 'version',
      key: 'version',
      width: 80,
    },
    {
      title: t('release.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (s) => (
        <Tag color={STATUS_COLOR[s]}>{t(`release.statuses.${s}`)}</Tag>
      ),
    },
    {
      title: t('release.publishedAt'),
      key: 'publishedAt',
      width: 200,
      render: (_, r) => (
        <>
          {dayjs(r.publishedAt).format('DD.MM.YYYY HH:mm')}
          {r.publishedBy && (
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              {`${r.publishedBy.firstName || ''} ${r.publishedBy.lastName || ''}`.trim()}
            </div>
          )}
        </>
      ),
    },
    {
      title: t('release.notified'),
      dataIndex: 'notifiedCount',
      key: 'notifiedCount',
      width: 100,
      align: 'right',
    },
    {
      title: t('release.delivered'),
      key: 'deliveries',
      width: 220,
      render: (_, r) =>
        (r.deliveries || []).map((d) => (
          <div key={d.email} style={{ fontSize: 12 }}>
            <Tag color={d.status === 'queued' ? 'green' : 'red'}>
              {t(`release.deliveryStatuses.${d.status}`)}
            </Tag>
            {d.email}
            {d.files?.length > 0 && (
              <div style={{ opacity: 0.7 }}>{d.files.join(', ')}</div>
            )}
          </div>
        )),
    },
    {
      title: t('release.copies'),
      key: 'copies',
      width: 200,
      render: (_, r) =>
        (r.copies || []).map((email) => (
          <div key={email} style={{ fontSize: 12 }}>
            {email}
          </div>
        )),
    },
    {
      title: t('release.files'),
      key: 'files',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          {r.files.map((f) => (
            <Button
              key={f.index}
              size="small"
              shape="round"
              icon={<DownloadOutlined />}
              onClick={() =>
                downloadMutation.mutate({ releaseId: r._id, index: f.index })
              }
            >
              {f.fileName}
            </Button>
          ))}
          {r.note && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.note}
            </Text>
          )}
        </Space>
      ),
    },
    {
      key: 'actions',
      width: 120,
      render: (_, r) =>
        r.status === 'published' ? (
          <Popconfirm
            title={t('release.withdrawConfirm')}
            okText={t('release.withdraw')}
            cancelText={t('fccs.cancel')}
            okButtonProps={{ danger: true, shape: 'round' }}
            cancelButtonProps={{ shape: 'round' }}
            onConfirm={() => withdrawMutation.mutate(r._id)}
          >
            <Button
              size="small"
              shape="round"
              danger
              icon={<RollbackOutlined />}
            >
              {t('release.withdraw')}
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <div>
      <Space wrap style={{ display: 'flex', marginBottom: 12 }}>
        <Button
          type="primary"
          className="btn-primary"
          icon={<SendOutlined />}
          disabled={!canPublish}
          onClick={openDialog}
        >
          {current ? t('release.republish') : t('release.publish')}
        </Button>
        {current && (
          <Text type="secondary">
            {t('release.alreadyPublished', {
              date: dayjs(current.publishedAt).format('DD.MM.YYYY HH:mm'),
              version: current.version,
            })}
          </Text>
        )}
      </Space>

      <Table
        scroll={{ x: 'max-content' }}
        rowKey="_id"
        size="small"
        loading={isLoading}
        columns={columns}
        dataSource={releases}
        pagination={{ pageSize: 12 }}
        locale={{ emptyText: t('release.none') }}
      />

      <Modal
        open={open}
        title={t('release.modalTitle', { period: label })}
        okText={t('release.publishOk')}
        cancelText={t('fccs.cancel')}
        okButtonProps={{ className: 'btn-primary', disabled: !confirmed }}
        cancelButtonProps={{ shape: 'round' }}
        // `.btn` (globals.css) makes the primary button a flex block, so the
        // footer's text-align no longer keeps the two buttons on one line.
        classNames={{ footer: 'flex justify-end gap-2' }}
        confirmLoading={publishMutation.isPending}
        onOk={() => publishMutation.mutate()}
        onCancel={() => setOpen(false)}
        width={760}
      >
        {open && (
          <FxCheckAlert companyId={companyId} year={year} month={month} />
        )}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('release.modalInfo')}
        />
        {current && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={t('release.modalReplaces')}
          />
        )}
        <div style={{ marginBottom: 16 }}>
          <RecipientsFields
            companyId={companyId}
            recipients={recipients}
            onRecipientsChange={setRecipients}
            copies={copies}
            onCopiesChange={setCopies}
            notifyClientUsers={notifyClientUsers}
            onNotifyChange={setNotifyClientUsers}
            fileOptions={fileOptions}
          />
        </div>
        <MailPreview
          companyId={companyId}
          recipients={recipients}
          copies={copies}
          notifyClientUsers={notifyClientUsers}
          fileOptions={fileOptions}
        />
        <Input.TextArea
          rows={3}
          maxLength={1000}
          placeholder={t('release.notePlaceholder')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Checkbox
          style={{ marginTop: 12 }}
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        >
          <Text strong>{t('monthlyShort.confirm')}</Text>
        </Checkbox>
      </Modal>
    </div>
  );
}
